import { useState } from "react";
import { Trash2, Plus, Download } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import StreakBanner from "@/components/earthpulse/StreakBanner";
import { toast } from "sonner";

// ─── CSV export ─────────────────────────────────────────────────────
function escapeCsv(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function entriesToCsv(entries) {
  const header = [
    "created_at",
    "mood_out_of_10",
    "energy_out_of_10",
    "clarity_out_of_10",
    "note",
    "schumann_freq_hz",
    "schumann_amplitude",
    "kp_index",
  ];
  const rows = entries.map((e) => [
    e.created_at,
    e.mood,
    e.energy,
    e.clarity,
    e.note || "",
    e.schumann_freq != null ? Number(e.schumann_freq).toFixed(3) : "",
    e.schumann_amp != null ? Number(e.schumann_amp).toFixed(2) : "",
    e.kp_index != null ? Number(e.kp_index).toFixed(2) : "",
  ]);
  return [header, ...rows].map((r) => r.map(escapeCsv).join(",")).join("\n");
}

function downloadCsv(entries) {
  if (!entries || entries.length === 0) {
    toast.error("No entries yet to export.");
    return;
  }
  const csv = entriesToCsv(entries);
  // Prepend BOM so Excel opens UTF-8 correctly
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const today = new Date().toISOString().slice(0, 10);
  const a = document.createElement("a");
  a.href = url;
  a.download = `earth-pulse-journal-${today}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  toast.success("Journal exported", { description: `${entries.length} entries · CSV downloaded.` });
}
// ────────────────────────────────────────────────────────────────────

function ScoreRow({ label, value, onChange, color, testid }) {
  const isSet = value !== null;
  const displayValue = isSet ? value : 5; // slider needs a numeric fallback for positioning
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="label-overline">{label}</span>
        <span
          className="font-serif-display text-2xl"
          style={{ color: isSet ? color : "var(--text-muted)" }}
          data-testid={`${testid}-value`}
        >
          {isSet ? value : "—"}
          {isSet && (
            <span className="text-[color:var(--text-muted)] text-sm ml-1 font-sans-body">/10</span>
          )}
        </span>
      </div>
      <Slider
        value={[displayValue]}
        min={1}
        max={10}
        step={1}
        onValueChange={(v) => onChange(v[0])}
        className={isSet ? "" : "opacity-60"}
        data-testid={testid}
      />
    </div>
  );
}

export default function JournalSection({ entries, onCreate, onDelete, submitting }) {
  // Sliders remain null until the user actually taps them — no "assumed" default of 6/10
  const [mood, setMood] = useState(null);
  const [energy, setEnergy] = useState(null);
  const [clarity, setClarity] = useState(null);
  const [note, setNote] = useState("");

  const ready = mood !== null && energy !== null && clarity !== null;

  const handleSubmit = () => {
    if (!ready) return;
    onCreate({ mood, energy, clarity, note });
    setMood(null);
    setEnergy(null);
    setClarity(null);
    setNote("");
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
      <div className="md:col-span-5 parchment-card rounded-2xl p-8 md:p-10" data-testid="journal-form">
        <div className="label-overline constellation-underline">Personal Journal</div>
        <h2 className="mt-3 font-serif-display text-white text-2xl md:text-3xl tracking-tight mb-2">
          How does it feel today?
        </h2>
        <p className="font-serif-italic text-[color:var(--text-secondary)] mb-8">
          Trace your inner weather beside the sky.
        </p>

        <div className="space-y-7">
          <ScoreRow label="Mood" value={mood} onChange={setMood} color="var(--gold)" testid="slider-mood" />
          <ScoreRow label="Energy" value={energy} onChange={setEnergy} color="var(--rose-gold)" testid="slider-energy" />
          <ScoreRow label="Clarity" value={clarity} onChange={setClarity} color="var(--parchment)" testid="slider-clarity" />

          <div>
            <div className="label-overline mb-2">Notes</div>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Sleep, dreams, focus, anything you noticed…"
              className="bg-[color:var(--bg-deeper)]/70 border-[color:var(--gold)]/40 focus-visible:ring-[color:var(--gold)] focus-visible:border-[color:var(--gold)] text-white font-medium text-sm rounded-lg min-h-[100px] font-sans-body placeholder:text-[color:var(--parchment-soft)]/60 placeholder:font-normal placeholder:italic"
              data-testid="journal-note"
              maxLength={800}
            />
          </div>

          <Button
            onClick={handleSubmit}
            disabled={submitting || !ready}
            className="w-full rounded-full h-12 bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-sans-body tracking-wide text-sm disabled:opacity-40"
            data-testid="journal-submit"
          >
            <Plus size={14} className="mr-2" />
            {submitting ? "Saving…" : (ready ? "Save entry" : "Tap each slider to begin")}
          </Button>
        </div>
      </div>

      <div className="md:col-span-7 parchment-card rounded-2xl p-8 md:p-10" data-testid="journal-list">
        <StreakBanner entries={entries} />
        <div className="flex items-center justify-between mb-8 gap-3">
          <div>
            <div className="label-overline constellation-underline">Journal Timeline</div>
            <h2 className="mt-3 font-serif-display text-white text-2xl md:text-3xl tracking-tight">
              Your entries, with the sky beside them
            </h2>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="label-overline hidden sm:inline">{entries.length} entries</span>
            <button
              type="button"
              onClick={() => downloadCsv(entries)}
              disabled={entries.length === 0}
              data-testid="journal-export-csv"
              aria-label="Export journal as CSV"
              title="Download all entries as CSV"
              className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--gold)]/40 hover:border-[color:var(--gold)] hover:bg-[color:var(--gold)]/10 disabled:opacity-40 disabled:cursor-not-allowed transition px-3 py-1.5 label-overline text-[10px] text-[color:var(--gold)]"
            >
              <Download size={11} /> Export CSV
            </button>
          </div>
        </div>

        {entries.length === 0 ? (
          <div className="text-[color:var(--text-muted)] text-base py-10 text-center font-serif-italic">
            No entries yet — begin on the left.
          </div>
        ) : (
          <ul className="space-y-4 max-h-[520px] overflow-y-auto pr-2">
            {entries.map((e) => (
              <li
                key={e.id}
                className="relative rounded-xl border border-[color:var(--gold)]/12 p-5 hover-lift"
                data-testid={`journal-entry-${e.id}`}
                style={{ background: "rgba(28, 38, 74, 0.4)" }}
              >
                {/* Delete button — absolutely positioned so it never steals width */}
                <button
                  onClick={() => onDelete(e.id)}
                  aria-label="delete entry"
                  data-testid={`journal-delete-${e.id}`}
                  className="absolute top-3 right-3 text-[color:var(--text-muted)] hover:text-[color:var(--accent-warning)] p-1.5 rounded-full hover:bg-[color:var(--accent-warning)]/10"
                  style={{ transition: "color 200ms ease, background 200ms ease" }}
                >
                  <Trash2 size={14} />
                </button>

                <div className="font-serif-italic text-[color:var(--text-secondary)] text-sm pr-8">
                  {new Date(e.created_at).toLocaleString(undefined, {
                    weekday: "short", month: "short", day: "numeric",
                    hour: "2-digit", minute: "2-digit",
                  })}
                </div>

                {/* Score row — flex so labels + values sit side-by-side and
                    naturally reflow if the card gets narrow. */}
                <div className="mt-4 flex flex-wrap items-baseline gap-x-6 gap-y-2">
                  <div className="flex items-baseline gap-2">
                    <span className="label-overline text-[10px]">Mood</span>
                    <span className="font-serif-display text-[color:var(--gold)] text-xl leading-none">
                      {e.mood}<span className="text-[color:var(--text-muted)] text-xs ml-0.5">/10</span>
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="label-overline text-[10px]">Energy</span>
                    <span className="font-serif-display text-[color:var(--rose-gold)] text-xl leading-none">
                      {e.energy}<span className="text-[color:var(--text-muted)] text-xs ml-0.5">/10</span>
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="label-overline text-[10px]">Clarity</span>
                    <span className="font-serif-display text-[color:var(--parchment)] text-xl leading-none">
                      {e.clarity}<span className="text-[color:var(--text-muted)] text-xs ml-0.5">/10</span>
                    </span>
                  </div>
                </div>

                {e.note && (
                  <p className="mt-4 text-[color:var(--parchment-soft)] leading-relaxed font-serif-italic text-sm">
                    {e.note}
                  </p>
                )}

                <div className="mt-4 pt-3 border-t border-[color:var(--gold)]/10 flex flex-wrap gap-x-4 gap-y-1 label-overline text-[10px] text-[color:var(--text-muted)]">
                  {e.schumann_freq != null && <span>SR · {e.schumann_freq.toFixed(2)} Hz</span>}
                  {e.schumann_amp != null && <span>Amp · {e.schumann_amp.toFixed(1)}</span>}
                  {e.kp_index != null && <span>Kp · {e.kp_index.toFixed(1)}</span>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
